import { ArrowDownIcon, ArrowUpIcon, EyeIcon, EyeSlashIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { convertNullToBlank } from 'pages/Resources/GuidelineOverlay/logic'
import { useState } from 'react'

import { Input2 } from '../Input2/Input2'
import { ISelectOption } from './types'

export const SelectOptionInputs = ({
  title = 'Select Options',
  value = [],
  allCanDelete = false,
  minLength = 0,
  showKey = false,
  showVisibleBtn = true,
  additionalElements = null,
  showAdjustments = false,
  onChange,
}: {
  title?: string
  value: ISelectOption[]
  allCanDelete?: boolean
  minLength?: number
  showKey?: boolean
  showVisibleBtn?: boolean
  additionalElements?: Function | JSX.Element | JSX.Element[] | null
  showAdjustments?: boolean
  onChange: Function
}) => {
  const onAddOption = () => {
    const newValue = cloneDeep(value || [])
    newValue.push({
      key: `${Date.now()}`,
      title: '',
      visible: true,
      isCustom: true,
    })
    onChange(newValue)
  }

  const [adjustmentDrafts, setAdjustmentDrafts] = useState<Record<string, string>>({})

  const getAdjustmentValue = (idx: number, field: 'LLPA' | 'LLRA', savedValue?: number) =>
    adjustmentDrafts[`${idx}-${field}`] ?? convertNullToBlank(savedValue)

  const onChangeAdjustmentDraft = (idx: number, field: 'LLPA' | 'LLRA', value: string) => {
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return
    setAdjustmentDrafts((drafts) => ({ ...drafts, [`${idx}-${field}`]: value }))
  }

  const onBlurAdjustment = (idx: number, field: 'LLPA' | 'LLRA', rawValue: string) => {
    const value = rawValue === '' || isNaN(Number(rawValue)) ? NaN : Number(rawValue)
    setAdjustmentDrafts((drafts) => {
      const nextDrafts = { ...drafts }
      delete nextDrafts[`${idx}-${field}`]
      return nextDrafts
    })
    onChangeControlOption(idx, { [field]: value })
  }

  const onChangeControlOption = (
    idx: number,
    newValue: { [P in keyof ISelectOption]?: NonNullable<ISelectOption[P]> } | null,
  ) => {
    const newValues = cloneDeep(value)

    if (!newValue) newValues.splice(idx, 1)
    else {
      newValues[idx] = {
        ...newValues[idx],
        ...newValue,
      }
    }

    onChange(newValues)
  }

  const onChangeControlOptionOrder = (idx: number, dir: 1 | -1) => {
    const newConfigs = cloneDeep(value)

    const newIdx = idx + dir
    if (newIdx < 0 || newIdx >= newConfigs.length) return
    else {
      let temp: ISelectOption = newConfigs[idx]
      newConfigs[idx] = newConfigs[newIdx]
      newConfigs[newIdx] = temp
    }

    onChange(newConfigs)
  }

  return (
    <div className="grid gap-2">
      <div className="flex justify-between">
        <p className="font-bold text-sm">{title}</p>
        <span className="btn-icon" onClick={onAddOption}>
          <PlusIcon className="w-4 h-4" />
        </span>
      </div>
      {value.map((opt, idx) => {
        const { key, title, visible, isCustom, LLPA, LLRA } = opt
        const isVisible = !!visible || visible === undefined
        const Icon = isVisible ? EyeIcon : EyeSlashIcon

        return (
          <div className={showAdjustments ? 'grid grid-cols-5 gap-2' : ''}>
            <div className={showAdjustments ? 'col-span-3' : ''}>
          <Input2
            title={`${idx + 1}. ${isCustom && !showKey ? '' : key}`}
            value={title}
            key={`Option-${idx}-${key}`}
            placeholder={title}
            className={isVisible ? 'opacity-100' : 'opacity-30'}
            onChange={(value: string) => onChangeControlOption(idx, { title: value })}
            additionalElements={() => (
              <div className="flex gap-2">
                {showVisibleBtn && (
                  <Icon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => onChangeControlOption(idx, { visible: !isVisible })}
                  />
                )}
                {(isCustom || allCanDelete) && value.length > minLength && (
                  <TrashIcon
                    className="w-4 h-4 cursor-pointer text-red-500"
                    onClick={() => onChangeControlOption(idx, null)}
                  />
                )}
                <ArrowUpIcon
                  className="w-4 h-4 cursor-pointer text-shade-blue"
                  onClick={() => onChangeControlOptionOrder(idx, -1)}
                />
                <ArrowDownIcon
                  className="w-4 h-4 cursor-pointer text-shade-blue"
                  onClick={() => onChangeControlOptionOrder(idx, 1)}
                />
                {additionalElements && typeof additionalElements == 'function'
                  ? additionalElements(opt, (value: Record<string, any>) => onChangeControlOption(idx, value))
                  : additionalElements}
              </div>
            )}
          />
            </div>
            {showAdjustments && (
              <>
                <Input2
                  type="text"
                  title="LLPA"
                  value={getAdjustmentValue(idx, 'LLPA', LLPA)}
                  key={`llpa-${idx}-${key}`}
                  className={isVisible ? 'opacity-100' : 'opacity-30'}
                  onChange={(value: string) => onChangeAdjustmentDraft(idx, 'LLPA', value)}
                  onBlur={(value: string) => onBlurAdjustment(idx, 'LLPA', value)}
                />
                <Input2
                  type="text"
                  title="LLRA"
                  value={getAdjustmentValue(idx, 'LLRA', LLRA)}
                  key={`llra-${idx}-${key}`}
                  className={isVisible ? 'opacity-100' : 'opacity-30'}
                  onChange={(value: string) => onChangeAdjustmentDraft(idx, 'LLRA', value)}
                  onBlur={(value: string) => onBlurAdjustment(idx, 'LLRA', value)}
                />
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
